import React, { Fragment, useEffect } from 'react';
import NumericInputElement, { TypeOnBlur } from '../common/numericinput';
import { formatCurrency, verifyAndReturnDecimal, currencySymbolFor } from '../common/common';
import { Box, Button, Grid, InputAdornment, TextField } from '@mui/material';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';

const styles = {
    textField: { ml: 1, mr: 1, width: '95%' },
};

export default function FilterOrderRowEdit(props) {
    const { t } = useTranslation();
    const saving = props.saving;

    const [foInEdit, setFoInEdit] = React.useState({});
    const foCache = React.useRef(null);

    useEffect(() => {
        if (props.filterOrder && foCache.current) {
            if (
                props.filterOrder.filterCount !== foCache.current.filterCount ||
                props.filterOrder.totalPriceWithoutVat !== foCache.current.totalPriceWithoutVat ||
                props.filterOrder.unitPriceWithoutVat !== foCache.current.unitPriceWithoutVat
            ) {
                foCache.current.filterCount = props.filterOrder.filterCount;
                foCache.current.totalPriceWithoutVat = props.filterOrder.totalPriceWithoutVat;
                foCache.current.unitPriceWithoutVat = props.filterOrder.unitPriceWithoutVat;
                setFoInEdit(foCache.current);
            }
        } else if (props.filterOrder && !foCache.current) {
            foCache.current = cloneDeep(props.filterOrder);
            setFoInEdit(foCache.current);
        }
    }, [props.filterOrder]);

    const getRowClass = () => {
        if (props.selected) return 'highlight';
        return '';
    };

    const filterCountChanged = async (id, value) => {
        const fo = cloneDeep(foCache.current);
        fo.count = value;
        foCache.current = fo;
        setFoInEdit(fo);
        props.filterOrderChanged(fo);
    };

    const onChange = (e) => {
        const fo = cloneDeep(foCache.current);
        fo[e.target.name] = e.target.value;
        foCache.current = fo;
        setFoInEdit(fo);
        props.filterOrderChanged(fo);
    };

    const closeEdit = () => {
        props.filterOrderChanged(foCache.current);
        foCache.current = null;
        props.closeRowEdit();
    };

    const handlePriceChange = (e) => {
        const value = verifyAndReturnDecimal(e.target.value);
        if (value && value >= 0) {
            const fo = cloneDeep(foCache.current);
            fo.unitPriceWithoutVat = value;
            foCache.current = fo;
            setFoInEdit(fo);
            props.filterOrderChanged(fo);
        }
    };

    return (
        <Fragment>
            <Box component="tr" className={getRowClass()} sx={{ display: { xs: 'none', md: 'table-row' } }}>
                <td></td>
                <td>
                    {!props.priceOnly && (
                        <div>
                            <TextField
                                required
                                error={false}
                                name="machineName"
                                label={t('location.machine')}
                                sx={styles.textField}
                                margin="normal"
                                value={foInEdit.machineName}
                                onChange={onChange}
                            />
                            <TextField
                                required
                                error={false}
                                name="machineLocation"
                                label={t('location.position')}
                                sx={styles.textField}
                                margin="normal"
                                value={foInEdit.machineLocation}
                                onChange={onChange}
                            />
                            <TextField
                                required
                                error={false}
                                name="machineArea"
                                label={t('location.serviceArea')}
                                sx={styles.textField}
                                margin="normal"
                                value={foInEdit.machineArea}
                                onChange={onChange}
                            />
                            <TextField
                                required
                                error={false}
                                name="palletInformation"
                                label={t('order.palletGroup')}
                                sx={styles.textField}
                                margin="normal"
                                value={foInEdit.palletInformation}
                                onChange={onChange}
                            />
                        </div>
                    )}
                    <br />
                    <Button color="primary" variant="contained" onClick={closeEdit}>
                        {t('buttons.close')}
                    </Button>
                </td>

                <td>
                    <strong>
                        {props.filterOrder.product
                            ? props.filterOrder.product.productName
                            : props.filterOrder.productBundle.name}
                    </strong>
                    {props.filterOrder.filterAdditionalInfo && (
                        <span>
                            <br />
                            <em>{props.filterOrder.filterAdditionalInfo}</em>
                        </span>
                    )}
                </td>
                <td className={'filter-count-' + props.filterOrder.id}>
                    {!props.priceOnly && (
                        <NumericInputElement
                            id={null}
                            value={foInEdit.count}
                            onChange={filterCountChanged}
                            type={TypeOnBlur}
                            min={1}
                            saving={saving}
                        />
                    )}
                </td>
                <td>
                    <TextField
                        name={`filterPrice-${props.filterOrder.id}`}
                        defaultValue={props.filterOrder.unitPriceRounded()}
                        onBlur={(e) => handlePriceChange(e)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">{currencySymbolFor(props.currency)}/kpl</InputAdornment>
                            ),
                        }}
                    />
                </td>
                <td>{formatCurrency(props.filterOrder.totalPriceWithoutVat, 2, props.currency)}</td>
            </Box>

            <Box component="tr" className={getRowClass()} sx={{ display: { xs: 'table-row', md: 'none' } }}>
                <td></td>
                <td>
                    <div>
                        <div>
                            <strong>
                                {props.filterOrder.product
                                    ? props.filterOrder.product.productName
                                    : props.filterOrder.productBundle.name}
                            </strong>
                        </div>
                        {!props.priceOnly && (
                            <React.Fragment>
                                <TextField
                                    required
                                    error={false}
                                    name="machineName"
                                    label={t('location.machine')}
                                    sx={styles.textField}
                                    margin="normal"
                                    value={foInEdit.machineName}
                                    onChange={onChange}
                                />
                                <TextField
                                    required
                                    error={false}
                                    name="machineLocation"
                                    label={t('location.position')}
                                    sx={styles.textField}
                                    margin="normal"
                                    value={foInEdit.machineLocation}
                                    onChange={onChange}
                                />
                                <TextField
                                    required
                                    error={false}
                                    name="machineArea"
                                    label={t('location.serviceArea')}
                                    sx={styles.textField}
                                    margin="normal"
                                    value={foInEdit.machineArea}
                                    onChange={onChange}
                                />
                                <TextField
                                    required
                                    error={false}
                                    name="palletInformation"
                                    label={t('order.palletGroup')}
                                    sx={styles.textField}
                                    margin="normal"
                                    value={foInEdit.palletInformation}
                                    onChange={onChange}
                                />
                            </React.Fragment>
                        )}
                    </div>
                    {!props.priceOnly && (
                        <NumericInputElement
                            id={null}
                            value={foInEdit.count}
                            onChange={filterCountChanged}
                            type={TypeOnBlur}
                            min={1}
                            saving={saving}
                        />
                    )}
                    <TextField
                        sx={styles.textField}
                        name={`filterPrice-${props.filterOrder.id}`}
                        defaultValue={props.filterOrder.unitPriceRounded()}
                        onBlur={(e) => handlePriceChange(e)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">{currencySymbolFor(props.currency)}/kpl</InputAdornment>
                            ),
                        }}
                    />
                    <br />
                    <Grid item xs={12} sx={{ mt: 1, ml: 1, mr: 1 }}>
                        {t('order.totalShort')}{' '}
                        <strong>{formatCurrency(props.filterOrder.totalPriceWithoutVat, 2, props.currency)}</strong>
                    </Grid>
                    <br />
                    <Button color="primary" variant="contained" onClick={closeEdit}>
                        {t('buttons.close')}
                    </Button>
                </td>
            </Box>
        </Fragment>
    );
}
